/* eslint-disable react/prop-types */
/* eslint-disable prettier/prettier */
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileTextOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import { Button, Modal, Tooltip, message } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { deleteSolicitation, getFilesFromSolicitation } from '../../operations/solicitations';

/* ─── Styled Components ─── */

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${({ $status }) => ($status === 'completed' ? '#f6ffed' : '#fffbe6')};
  border: 1px solid ${({ $status }) => ($status === 'completed' ? '#b7eb8f' : '#ffe58f')};
  border-radius: 10px;
  margin-bottom: 24px;
  gap: 12px;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProtocolLabel = styled.span`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ProtocolValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.85);
  font-variant-numeric: tabular-nums;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.3);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  font-size: 14px;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
    color: rgba(0, 0, 0, 0.65);
  }
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 20px;
  background: ${({ $status }) => ($status === 'completed' ? '#52c41a' : '#fa8c16')};
  color: #fff;
  white-space: nowrap;
  flex-shrink: 0;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;
  margin-bottom: 24px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MetaLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const MetaValue = styled.span`
  font-size: 14px;
  color: rgba(0, 0, 0, 0.75);
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;

  .anticon {
    font-size: 16px;
    color: rgba(0, 0, 0, 0.35);
  }
`;

const SectionTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`;

const NfeCard = styled.div`
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const NfeCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
  flex-wrap: wrap;
`;

const NfeCardName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`;

const NfeCardAmount = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1890ff;
`;

const NfeCardMeta = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.45);
`;

const NfeCardMetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const ObservationBox = styled.div`
  padding: 14px 16px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
  line-height: 1.6;
`;

const AnswerBox = styled.div`
  padding: 16px;
  background: ${({ $hasAnswer }) => ($hasAnswer ? '#f6ffed' : '#fafafa')};
  border: 1px solid ${({ $hasAnswer }) => ($hasAnswer ? '#b7eb8f' : '#f0f0f0')};
  border-radius: 8px;
  font-size: 14px;
  color: ${({ $hasAnswer }) => ($hasAnswer ? 'rgba(0, 0, 0, 0.75)' : 'rgba(0, 0, 0, 0.25)')};
  line-height: 1.6;
  ${({ $hasAnswer }) => (!$hasAnswer ? 'font-style: italic;' : '')}
`;

const FileCard = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;

  &:hover {
    border-color: #1890ff;
    background: #e6f7ff;
  }

  .anticon {
    font-size: 20px;
    color: #1890ff;
  }
`;

const FileName = styled.span`
  flex: 1;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.75);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DownloadIcon = styled.span`
  color: rgba(0, 0, 0, 0.25);
  font-size: 16px;
  transition: color 0.2s;

  ${FileCard}:hover & {
    color: #1890ff;
  }
`;

const StyledModal = styled(Modal)`
  @media (max-width: 480px) {
    top: 0;
    max-width: 100vw;
    margin: 0;
    padding: 0;

    .ant-modal-content {
      border-radius: 0;
      min-height: 100vh;
    }

    .ant-modal-body {
      padding: 16px !important;
    }

    .ant-modal-close {
      top: 8px;
      right: 8px;
    }
  }
`;

/* ─── Component ─── */

const ViewSolicitation = ({ solicitation, onClose, onDelete }) => {
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!solicitation) return;

    const fetchFiles = async () => {
      try {
        const uploadedFiles = await getFilesFromSolicitation(solicitation.uid);
        if (!uploadedFiles) return;
        setFiles(uploadedFiles.map((file) => ({
          uid: file.id,
          name: file.original_name,
          status: 'done',
          url: file.file_url,
        })));
      } catch (err) {
        console.error('Failed to load files:', err);
      }
    };
    fetchFiles();
  }, [solicitation && solicitation.id]);

  if (!solicitation) return null;

  const { description, status, created_at, awnser } = solicitation;
  const statusKey = status === 1 ? 'completed' : 'pending';
  const customData = solicitation.custom_data;
  const nfeItems = Array.isArray(customData) ? customData : [];
  const uploadFile = customData && customData.upload_file;

  const copyProtocol = () => {
    if (!solicitation.protocol) return;
    navigator.clipboard.writeText(solicitation.protocol);
    message.success('Protocolo copiado!');
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Excluir solicitação',
      content: 'Tem certeza de que deseja excluir esta solicitação? Esta ação não poderá ser desfeita.',
      okText: 'Excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk() {
        return deleteSolicitation(id).then(() => {
          onDelete(id);
        }).catch(() => {
          message.error('Erro ao excluir solicitação');
        });
      },
    });
  };

  return (
    <StyledModal
      title={null}
      open={!!solicitation}
      onCancel={onClose}
      width={680}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span
            role="button"
            tabIndex={0}
            onClick={() => handleDelete(solicitation.id)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              color: 'rgba(0,0,0,0.3)',
              cursor: 'pointer',
              padding: '4px 0',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#ff4d4f'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(0,0,0,0.3)'; }}
          >
            <DeleteOutlined />
            Excluir
          </span>
          <Button type="primary" size="large" onClick={onClose} style={{ borderRadius: 8, fontWeight: 600 }}>
            Fechar
          </Button>
        </div>
      }
      bodyStyle={{ padding: '24px' }}
    >
      {/* Header with protocol + status */}
      <HeaderBar $status={statusKey}>
        <HeaderLeft>
          <ProtocolLabel>Protocolo</ProtocolLabel>
          <ProtocolValue>
            {solicitation.protocol || '—'}
            {solicitation.protocol && (
              <Tooltip title="Copiar protocolo">
                <CopyButton onClick={copyProtocol}>
                  <CopyOutlined />
                </CopyButton>
              </Tooltip>
            )}
          </ProtocolValue>
        </HeaderLeft>
        <StatusPill $status={statusKey}>
          {status === 1
            ? <><CheckCircleOutlined /> Emitida</>
            : <><ClockCircleOutlined /> Aguardando</>
          }
        </StatusPill>
      </HeaderBar>

      {/* Meta info grid */}
      <MetaGrid>
        <MetaItem>
          <MetaLabel>Data da solicitação</MetaLabel>
          <MetaValue>{moment(created_at).format('DD/MM/YYYY [às] HH:mm')}</MetaValue>
        </MetaItem>
        <MetaItem>
          <MetaLabel>Tipo</MetaLabel>
          <MetaValue>{solicitation.solicitation_type}</MetaValue>
        </MetaItem>
        {uploadFile && (
          <MetaItem>
            <MetaLabel>Arquivo enviado</MetaLabel>
            <MetaValue>{uploadFile.original_name}</MetaValue>
          </MetaItem>
        )}
      </MetaGrid>

      {/* NF-e items as cards */}
      {nfeItems.length > 0 && (
        <Section>
          <SectionHeader>
            <FileTextOutlined />
            <SectionTitle>Notas Fiscais ({nfeItems.length})</SectionTitle>
          </SectionHeader>
          {nfeItems.map((item, index) => (
            <NfeCard key={item.key || index}>
              <NfeCardHeader>
                <NfeCardName>{item.name || 'Sem nome'}</NfeCardName>
                <NfeCardAmount>{item.amount ? 'R$ ' + item.amount : ''}</NfeCardAmount>
              </NfeCardHeader>
              <NfeCardMeta>
                {item.doc && <NfeCardMetaItem>{item.doc}</NfeCardMetaItem>}
                {item.cod && <NfeCardMetaItem>Código: {item.cod}</NfeCardMetaItem>}
                {item.description && <NfeCardMetaItem>{item.description}</NfeCardMetaItem>}
              </NfeCardMeta>
            </NfeCard>
          ))}
        </Section>
      )}

      {/* Observations */}
      {description && (
        <Section>
          <SectionHeader>
            <FileTextOutlined />
            <SectionTitle>Observações</SectionTitle>
          </SectionHeader>
          <ObservationBox>{description}</ObservationBox>
        </Section>
      )}

      {/* Accountant response */}
      <Section>
        <SectionHeader>
          <CheckCircleOutlined />
          <SectionTitle>Retorno do Contador</SectionTitle>
        </SectionHeader>
        <AnswerBox $hasAnswer={!!awnser}>
          {awnser || 'Aguardando retorno do contador...'}
        </AnswerBox>
      </Section>

      {/* Response files */}
      {files.length > 0 && (
        <Section>
          <SectionHeader>
            <PaperClipOutlined />
            <SectionTitle>Notas Fiscais Emitidas ({files.length})</SectionTitle>
          </SectionHeader>
          {files.map((file) => (
            <FileCard key={file.uid} href={file.url} target="_blank" rel="noopener noreferrer">
              <FileTextOutlined />
              <FileName>{file.name}</FileName>
              <DownloadIcon>
                <DownloadOutlined />
              </DownloadIcon>
            </FileCard>
          ))}
        </Section>
      )}
    </StyledModal>
  );
};

export default ViewSolicitation;
